// askSmart-ui/src/pages/TOSP/estoppelValues.ts
//
// What goes on one tenant's estoppel certificate, worked out from TOSP.
//
// Computed here rather than on the server because the figures already have a
// home here: the rent is the expense statement's, and the contract dates are
// the lease card's. Calling the same functions means a certificate can never
// disagree with the screen it is checked against. The backend only formats
// and places what it is sent.

import { publishedStatement, tenantExpenseYear } from "./portal/tenantExpenses";
import {
  Building,
  BudgetYear,
  dateUnderContract,
  nextRenewalDate,
  Tenant,
  TospConfiguration,
  Unit,
} from "./types";

export interface EstoppelSpace {
  name: string;
  kind: "commercial" | "patio" | "basement" | "storage";
  sqft: number | null;
}

/** Mirrors EstoppelValues in the backend's estoppelDocument.ts. */
export interface EstoppelValues {
  tenantName: string;
  tenantLegal: string;
  landlord: string;
  commercialUnit: string;
  gscc: string;
  buildingName: string;
  leaseCommenceDate: string;
  /** When the lease was signed. Blank on most: the day is unfilled in the document. */
  leaseExecutionDate?: string;
  dateUnderContract: string | null;
  nextRenewalDate: string | null;
  spaces: EstoppelSpace[];
  storageOnly: boolean;
  baseRentTotal: number | null;
  storageRentTotal: number | null;
  prepaidRent: number | null;
  deposit: number | null;
}

export interface EstoppelDraft {
  values: EstoppelValues | null;
  /** What would print blank or wrong. Shown beside the tenant; doesn't block. */
  problems: string[];
}

interface Inputs {
  tenant: Tenant;
  year: number;
  unitById: (id: string) => Unit | undefined;
  buildings: Building[];
  configuration: TospConfiguration;
  /** So a published year quotes the figures that were posted, not fresh ones. */
  budgets: Pick<BudgetYear, "year" | "history" | "publishedSnapshot">[];
}

/**
 * What each leased area IS, for the certificate.
 *
 * The unit's Space Type decides most of it — but basements are typed Storage
 * on purpose, to keep them out of the BMVA fee. A basement is still part of
 * the demised premises, so an area NAMED basement or mezzanine is read by its
 * name first.
 */
function kindOf(name: string, unit: Unit | undefined): EstoppelSpace["kind"] {
  if (/basement|mezz/i.test(name)) return "basement";
  const type = unit?.spaceType || "";
  if (/storage|locker/i.test(type)) return "storage";
  if (/patio|terrace/i.test(type) || /patio|terrace/i.test(name)) return "patio";
  if (/basement|mezz/i.test(type)) return "basement";
  return "commercial";
}

export function estoppelDraft({
  tenant,
  year,
  unitById,
  buildings,
  configuration,
  budgets,
}: Inputs): EstoppelDraft {
  const problems: string[] = [];
  const leases = tenant.occupancies || [];
  if (leases.length !== 1) {
    return {
      values: null,
      problems: [leases.length === 0 ? "No lease recorded" : `${leases.length} leases — one certificate each isn't supported yet`],
    };
  }
  const lease = leases[0];

  const areas = lease.leasedSpaces.map((space) => {
    const unit = space.unitId ? unitById(space.unitId) : undefined;
    return { space, unit, kind: kindOf(space.name, unit) };
  });
  if (areas.length === 0) problems.push("No leased areas");
  for (const a of areas) {
    if (!a.unit) problems.push(`"${a.space.name}" has no unit`);
    else if (!a.unit.occupancySqFt) problems.push(`Unit ${a.unit.suiteNumber} has no square footage`);
  }

  // A storage-only licensee has no shop: its storage IS the premises.
  const storageOnly = areas.length > 0 && areas.every((a) => a.kind === "storage");
  const lead = areas.find((a) => a.kind === "commercial") || (storageOnly ? areas[0] : undefined);
  if (!lead) problems.push("No shop unit to name as the premises");
  const building = lead?.unit ? buildings.find((b) => b.id === lead.unit!.buildingId) : undefined;
  if (lead && !building?.gsccNumber) problems.push(`No GSCC # on ${building?.name || "the building"}`);

  // Rent the way the certificate reads it: premises (basements included)
  // apart from true storage. The statement is run over each set separately
  // and its rent lines taken whole.
  const isStorage = (spaceId: string) => areas.find((a) => a.space.id === spaceId)?.kind === "storage";
  const rentOf = (keep: (spaceId: string) => boolean): number | null => {
    const only: Tenant = {
      ...tenant,
      occupancies: [{ ...lease, leasedSpaces: lease.leasedSpaces.filter((s) => keep(s.id)) }],
    };
    const statement = tenantExpenseYear({ tenant: only, year, unitById, configuration, taxRolls: [], sales: [] });
    const total = (key: string) => statement.lines.find((l) => l.key === key)?.total ?? 0;
    const sum = total("baseRent") + total("storageRent");
    return sum ? sum : null;
  };
  /**
   * A published year's rent comes from its frozen statement — BUT ONLY WHERE
   * THE TWO ARE THE SAME QUESTION.
   *
   * A frozen statement covers the whole tenancy; the certificate wants rent
   * split by which spaces are premises and which are storage, worked out by
   * running the statement over each subset. Those agree exactly when the
   * tenancy is one lease, because then the frozen Base Rent and Storage Rent
   * lines ARE that split. With two leases they do not, and a certificate is
   * the wrong place to be approximately right — so that case still computes.
   */
  const frozen = publishedStatement(budgets, tenant.id, year);
  const frozenTotal = (key: string) =>
    frozen?.lines.find((line) => line.key === key)?.total ?? 0;
  const useFrozen = !!frozen && (tenant.occupancies || []).length === 1;
  const orNull = (value: number) => (value ? value : null);

  const baseRentTotal = useFrozen
    ? orNull(
        storageOnly
          ? frozenTotal("baseRent") + frozenTotal("storageRent")
          : frozenTotal("baseRent"),
      )
    : storageOnly
      ? rentOf(() => true)
      : rentOf((id) => !isStorage(id));

  const storageRentTotal = storageOnly
    ? null
    : useFrozen
      ? orNull(frozenTotal("storageRent"))
      : rentOf(isStorage);
  if (!baseRentTotal) problems.push(`No ${year} rent for the premises`);

  const landlord = (lease.landlordLegalEntity || "").trim();
  if (!landlord) problems.push("No Landlord Legal Entity");
  if (!tenant.legalEntity.trim()) problems.push("No legal entity");
  if (!lease.leaseCommenceDate) problems.push("No lease commencement date");
  const underContract = dateUnderContract(lease);
  const nextRenewal = nextRenewalDate(lease);
  if (!underContract || !nextRenewal) problems.push("Lease term has run out — no renewal date to state");

  return {
    problems,
    values: {
      tenantName: tenant.operatingName,
      tenantLegal: tenant.legalEntity,
      landlord,
      commercialUnit: lead?.unit?.suiteNumber || "",
      gscc: building?.gsccNumber || "",
      buildingName: building?.name || "",
      leaseCommenceDate: lease.leaseCommenceDate,
      leaseExecutionDate: lease.leaseExecutionDate,
      dateUnderContract: underContract,
      nextRenewalDate: nextRenewal,
      spaces: areas.map((a) => ({ name: a.space.name, kind: a.kind, sqft: a.unit?.occupancySqFt ?? null })),
      storageOnly,
      baseRentTotal,
      storageRentTotal,
      prepaidRent: tenant.prepaidRentAmount ?? null,
      deposit: tenant.depositPaidAmount ?? lease.securityDepositAmount ?? null,
    },
  };
}

/** "2026 Estoppel Certificate - MJ Byrne's Irish Pub.pdf" */
export const estoppelFileName = (year: number, tenant: Tenant): string =>
  `${year} Estoppel Certificate - ${tenant.operatingName.trim()}.pdf`;
